import { useAuth } from "../context/AuthContext";
import { fileUrl } from "../lib/api";

// Official company letterhead (name AR/EN + logo + CR + VAT) — pulled from company profile,
// so it updates automatically whenever the profile is edited. Used on printed/exported docs.
export const Letterhead = ({ company: injected }) => {
  const auth = useAuth();
  const c = injected || auth?.company || {};
  const logo = c.logo_id ? fileUrl(c.logo_id) : null;
  return (
    <div data-testid="letterhead" className="mb-4 flex items-center justify-between gap-3 border-b-2 border-slate-800/80 pb-3" dir="rtl">
      <div className="min-w-0">
        <h2 className="font-heading text-lg font-extrabold leading-tight text-slate-900 truncate">{c.name || "شركتك"}</h2>
        {c.name_en && <div className="text-xs font-semibold text-slate-500 truncate" dir="ltr">{c.name_en}</div>}
        <div className="mt-1 space-y-0.5 text-[11px] text-slate-500">
          {c.cr_number && <div>سجل تجاري / C.R.: <span dir="ltr" className="font-mono-nums">{c.cr_number}</span></div>}
          {c.vat_number && <div>الرقم الضريبي / VAT: <span dir="ltr" className="font-mono-nums">{c.vat_number}</span></div>}
          {(c.phone || c.address) && <div>{[c.phone, c.address].filter(Boolean).join(" · ")}</div>}
        </div>
      </div>
      {logo && <img src={logo} alt="logo" crossOrigin="anonymous" className="h-16 w-16 shrink-0 rounded-lg object-contain" />}
    </div>
  );
};

// HTML-string version for print windows opened via document.write (no React).
export const letterheadHTML = (c = {}) => {
  const logo = c.logo_id ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${c.logo_id}` : null;
  const rows = [];
  if (c.cr_number) rows.push(`سجل تجاري / C.R.: <span style="direction:ltr;display:inline-block">${c.cr_number}</span>`);
  if (c.vat_number) rows.push(`الرقم الضريبي / VAT: <span style="direction:ltr;display:inline-block">${c.vat_number}</span>`);
  const contact = [c.phone, c.address].filter(Boolean).join(" · ");
  if (contact) rows.push(contact);
  return `<div style="display:flex;align-items:center;justify-content:space-between;gap:12px;border-bottom:2px solid #1e293b;padding-bottom:12px;margin-bottom:16px">
    <div>
      <div style="font-size:19px;font-weight:800;color:#0f172a">${c.name || "شركتك"}</div>
      ${c.name_en ? `<div style="font-size:12px;font-weight:600;color:#64748b;direction:ltr">${c.name_en}</div>` : ""}
      <div style="font-size:11px;color:#64748b;margin-top:4px;line-height:1.7">${rows.join("<br/>")}</div>
    </div>
    ${logo ? `<img src="${logo}" style="height:64px;width:64px;object-fit:contain;border-radius:8px"/>` : ""}
  </div>`;
};

// Official/manager signature block for printed receipts (image + name + title).
export const withSigner = (c = {}, d = {}) => (d?.signer_signature_id
  ? { ...c, official_name: d.signer_name || c.official_name, official_title: d.signer_title || c.official_title, signature_id: d.signer_signature_id, stamp_id: d.signer_stamp_id || c.stamp_id }
  : c);

export const withDept = (c = {}, dept) => {
  const s = c?.dept_signers?.[dept];
  return s?.signature_id ? { ...c, official_name: s.name || c.official_name, official_title: s.title || c.official_title, signature_id: s.signature_id } : c;
};

export const officialSignatureHTML = (c = {}) => {
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/files`;
  const img = c.signature_id ? `${base}/${c.signature_id}` : null;
  const stamp = c.stamp_id ? `${base}/${c.stamp_id}` : null;
  const title = c.official_title || "المسؤول";
  const name = c.official_name || "";
  const sigInner = img
    ? `<img src="${img}" crossorigin="anonymous" style="height:${c.sig_size || 140}px;max-width:340px;object-fit:contain"/>`
    : `<div style="width:220px;height:70px;border-bottom:1px solid #94a3b8"></div>`;
  const stampImg = stamp
    ? `<div style="margin-top:8px"><img src="${stamp}" crossorigin="anonymous" style="height:${c.stamp_size || 190}px;max-width:340px;object-fit:contain"/></div>`
    : "";
  return `<div style="text-align:center;min-width:240px">
    <div style="min-height:140px;display:flex;align-items:flex-end;justify-content:center">${sigInner}</div>
    <div style="border-top:1px solid #94a3b8;margin-top:2px;padding-top:5px;font-size:14px;font-weight:700;color:#334155">${title}${name ? `: ${name}` : ""}</div>
    ${stampImg}
    <div style="font-size:11px;color:#64748b;margin-top:4px">توقيع المسؤول والختم</div>
  </div>`;
};
